<template>
  <div v-if="hasVisibleContent" data-test="meal-plan-section" class="px-2 md:px-0 md:w-1/2 mx-auto mb-8">
    <div class="flex justify-start items-center">
      <div class="text-xl font-bold text-gray-brand-900 tracking-tighter">Meal Plan</div>
    </div>
    <div class="mt-4 md:mt-6 bg-white">
      <div class="rounded-lg border" :class="cardShadowClasses">
        <div class="pt-4 pb-4 px-6">
          <div v-if="planLine" class="text-md font-semibold text-brand-gray-800 tracking-tight">
            {{ planLine }}
          </div>
          <div v-if="statusDisplay" class="text-xs tracking-wide text-brand-900 mt-1">
            {{ statusDisplay }}
          </div>
          <div class="text-sm text-gray-brand-700 mt-2">
            <span v-if="isPaused">Paused until {{ pausedUntilFormatted }}</span>
            <span v-else-if="nextBillingFormatted">Next billing: {{ nextBillingFormatted }}</span>
          </div>
          <div v-if="remainingMealCredits != null" class="text-sm text-gray-brand-700 mt-1">
            Meals remaining: {{ remainingMealCredits }}
          </div>
          <div class="mt-4">
            <button
              v-if="!isPaused"
              type="button"
              class="px-4 py-2 text-gray-brand-600 border border-gray-brand-600 font-semibold text-xs rounded-full bg-white hover:bg-gray-brand-100"
              :disabled="isLoading"
              @click="openSkipConfirm"
            >
              Skip next billing cycle
            </button>
            <button
              v-else
              type="button"
              class="px-4 py-2 text-white border border-transparent font-semibold text-xs rounded-full bg-gray-900 hover:border-gray-brand-600"
              :disabled="isLoading"
              @click="resumeBilling"
            >
              Resume billing
            </button>
            <button
              type="button"
              data-test="cancel-plan-button"
              class="px-4 py-2 text-red-600 border border-red-600 font-semibold text-xs rounded-full bg-white hover:bg-red-50 ml-2"
              :disabled="isLoading"
              @click="openCancelConfirm"
            >
              Cancel plan
            </button>
          </div>
        </div>
      </div>
    </div>

    <Modal :is-open="showSkipConfirm" @close="closeSkipConfirm">
      <template #title>
        <h2 class="text-2xl font-bold text-gray-brand-900 tracking-tighter cursor-pointer align-middle">
          Skip next billing cycle
        </h2>
      </template>

      <template #body>
        <div class="sm:flex sm:items-start">
          <div class="w-full">
            <p class="text-gray-900">
              Skip your next billing cycle? You will not be billed or receive meals for that cycle.
            </p>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="sm:flex sm:flex-row gap-1">
          <button
            type="button"
            class="px-4 py-2 text-white border border-transparent italic font-semibold text-xs rounded-full bg-gray-900 hover:border-gray-brand-600 sm:mr-2"
            @click="closeSkipConfirm"
          >
            Back
          </button>
          <button
            type="button"
            class="px-4 py-2 text-gray-brand-600 border border-gray-brand-600 italic font-semibold text-xs rounded-full bg-white hover:bg-gray-brand-100"
            :disabled="isLoading"
            @click="confirmSkip"
          >
            Confirm Skip
          </button>
        </div>
      </template>
    </Modal>

    <Modal :is-open="showCancelConfirm" @close="closeCancelConfirm">
      <template #title>
        <h2 class="text-2xl font-bold text-gray-brand-900 tracking-tighter cursor-pointer align-middle">Cancel plan</h2>
      </template>

      <template #body>
        <div class="sm:flex sm:items-start">
          <div class="w-full">
            <p class="text-gray-900">
              Cancel your meal plan? You will no longer be billed
              {{ mealPlan?.attributes?.price_formatted }}/{{ cadenceWord }} and will stop receiving included meals. Any
              unused meals stay available until they expire, and you can rejoin at any time.
            </p>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="sm:flex sm:flex-row gap-1">
          <button
            type="button"
            class="px-4 py-2 text-white border border-transparent italic font-semibold text-xs rounded-full bg-gray-900 hover:border-gray-brand-600 sm:mr-2"
            @click="closeCancelConfirm"
          >
            Back
          </button>
          <button
            type="button"
            data-test="confirm-cancel-button"
            class="px-4 py-2 text-white border border-transparent italic font-semibold text-xs rounded-full bg-red-600 hover:bg-red-700"
            :disabled="isLoading"
            @click="confirmCancel"
          >
            Cancel my plan
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import mapRelationship from '@/utils/mapRelationship'
import Modal from '@/components/Molecules/Modal'
import { cardShadowClasses } from '@/config'

const CADENCE_WORDS = {
  weekly: 'week',
  monthly: 'month',
  every_two_weeks: 'two weeks'
}

export default {
  name: 'MealPlanSection',
  components: {
    Modal
  },
  data() {
    return {
      cardShadowClasses,
      showSkipConfirm: false,
      showCancelConfirm: false,
      isLoading: false
    }
  },
  computed: {
    customer() {
      return this.$store.getters.customer
    },
    mealPlanSubscription() {
      return mapRelationship(this.$store.state, this.customer?.relationships?.meal_plan_subscription?.data)
    },
    mealPlan() {
      return mapRelationship(this.$store.state, this.mealPlanSubscription?.relationships?.meal_plan?.data)
    },
    status() {
      return this.mealPlanSubscription?.attributes?.status
    },
    hasVisibleContent() {
      return !!this.mealPlanSubscription && this.status !== 'cancelled'
    },
    remainingMealCredits() {
      const credits = this.mealPlanSubscription?.attributes?.remaining_meal_credits
      return credits == null ? null : credits
    },
    skippedThroughDate() {
      return this.parseDate(this.mealPlanSubscription?.attributes?.skipped_through)
    },
    isPaused() {
      if (!this.skippedThroughDate) return false
      return this.skippedThroughDate >= this.today()
    },
    pausedUntilFormatted() {
      if (!this.skippedThroughDate) return ''
      const resumesOn = new Date(this.skippedThroughDate)
      resumesOn.setDate(resumesOn.getDate() + 1)
      return this.formatDate(resumesOn)
    },
    nextBillingFormatted() {
      return this.formatDate(this.parseDate(this.mealPlanSubscription?.attributes?.next_payment_date))
    },
    statusDisplay() {
      return this.humanizeStatus(this.status)
    },
    cadenceWord() {
      const interval = this.mealPlan?.attributes?.billing_interval
      return CADENCE_WORDS[interval] || interval || ''
    },
    planLine() {
      const attrs = this.mealPlan?.attributes
      if (!attrs) return ''
      return `${attrs.item_count} meals per ${this.cadenceWord} - ${attrs.price_formatted}/${this.cadenceWord}`
    }
  },
  methods: {
    today() {
      const now = new Date()
      now.setHours(0, 0, 0, 0)
      return now
    },
    parseDate(dateStr) {
      if (!dateStr) return null
      const [year, month, day] = dateStr.split('-').map(Number)
      return new Date(year, month - 1, day)
    },
    formatDate(date) {
      if (!date) return ''
      return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      }).format(date)
    },
    humanizeStatus(status) {
      if (!status) return ''
      return status
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    refetch() {
      return this.$store.dispatch('getCustomerInfo', ['meal_plan_subscription', 'meal_plan_subscription.meal_plan'])
    },
    openSkipConfirm() {
      this.showSkipConfirm = true
    },
    closeSkipConfirm() {
      this.showSkipConfirm = false
    },
    confirmSkip() {
      this.isLoading = true
      this.$store
        .dispatch('pauseMealPlan')
        .then(() => {
          this.showSkipConfirm = false
          return this.refetch()
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    openCancelConfirm() {
      this.showCancelConfirm = true
    },
    closeCancelConfirm() {
      this.showCancelConfirm = false
    },
    confirmCancel() {
      this.isLoading = true
      this.$store
        .dispatch('cancelMealPlan')
        .then(() => {
          this.showCancelConfirm = false
          return this.refetch()
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    resumeBilling() {
      const subscriptionId = this.mealPlanSubscription?.id
      if (!subscriptionId) return
      this.isLoading = true
      this.$store
        .dispatch('unpauseMealPlan', subscriptionId)
        .then(() => this.refetch())
        .finally(() => {
          this.isLoading = false
        })
    }
  }
}
</script>
